import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, DatePicker, Descriptions, Form, Input, Modal, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, PlusOutlined, RightOutlined, InboxOutlined, WarningOutlined, CloseCircleOutlined, SwapOutlined, BarChartOutlined, CheckCircleOutlined, BarcodeOutlined, ClearOutlined, ExportOutlined, FilterOutlined } from "@ant-design/icons";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import "./PackingMaterialPage.css";
import { useAuth } from "../Context/AuthContext";
import {
  fetchPackingMaterials,
  createPackingMaterial,
  receiveStock,
  fetchBatches,
  fetchMovements,
  fetchAuditLogs,
  fetchInventorySummary,
  fetchLowStockAlerts,
} from "../services/packingMaterialService";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const svgProps = { viewBox: "0 0 48 48", width: "1em", height: "1em", fill: "none", stroke: "currentColor", strokeWidth: 3, strokeLinejoin: "round", strokeLinecap: "round", "aria-hidden": true };

const CubeIcon = () => (
  <svg {...svgProps}>
    <path d="M24 5 41 14.5v19L24 43 7 33.5v-19z" />
    <path d="M7 14.5 24 24l17-9.5M24 24v19" />
    <path d="M15.5 9.8 32.5 19.2V26" />
  </svg>
);

const WarehouseIcon = () => (
  <svg {...svgProps} strokeWidth={3.2}>
    <path d="M5 19 24 7l19 12" />
    <path d="M9 17v24h30V17" />
    <rect x="16" y="25" width="16" height="16" rx="1.5" fill="currentColor" stroke="none" />
    <path d="M16 31h16M16 36h16" stroke="var(--pm-tile-bg)" strokeWidth={2} />
  </svg>
);

const ChartIcon = () => (
  <svg {...svgProps}>
    <rect x="9" y="25" width="8" height="15" rx="1.5" fill="currentColor" stroke="none" />
    <rect x="20" y="9" width="8" height="31" rx="1.5" fill="currentColor" stroke="none" />
    <rect x="31" y="19" width="8" height="21" rx="1.5" fill="currentColor" stroke="none" />
    <path d="M6 44h36" />
  </svg>
);

const HeroArt = () => (
  <svg viewBox="0 0 540 330" role="img" aria-label="Packing boxes, tape and bubble wrap" className="pm-hero-svg">
    <defs>
      <pattern id="pm-bubbles" width="12" height="12" patternUnits="userSpaceOnUse">
        <rect width="12" height="12" fill="#f4f7f5" />
        <circle cx="6" cy="6" r="4.2" fill="#fff" stroke="#dbe3df" strokeWidth="1" />
      </pattern>
    </defs>
    <path d="M430 62A70 70 0 0 1 512 44" stroke="#bfe6cf" strokeWidth="10" strokeLinecap="round" fill="none" />
    <path d="M470 96q8-16 22-14-2 16-22 14z" fill="#bfe6cf" />
    <ellipse cx="300" cy="292" rx="230" ry="20" fill="rgba(20,60,40,.10)" />
    <polygon points="110,262 232,236 322,270 200,298" fill="#e9d5b8" />
    <polygon points="108,258 230,232 320,266 198,294" fill="#f1e2ca" />
    <polygon points="160,178 226,154 292,178 226,204" fill="#e2c096" />
    <polygon points="160,178 226,204 226,268 160,242" fill="#c9975f" />
    <polygon points="226,204 292,178 292,242 226,268" fill="#b5834e" />
    <path d="M182 214q12-14 26-8-2 16-26 8z" fill="#2fa562" />
    <text x="176" y="232" fontSize="8" fontWeight="700" fill="#6b4a2a">THE GOOD</text>
    <text x="176" y="241" fontSize="8" fontWeight="700" fill="#6b4a2a">HEALTH</text>
    <polygon points="245,92 335,54 425,92 335,130" fill="#e2c096" />
    <polygon points="245,92 335,130 335,258 245,218" fill="#c9975f" />
    <polygon points="335,130 425,92 425,218 335,258" fill="#b5834e" />
    <polygon points="292,74 380,110 366,116 278,80" fill="#ecd2a8" />
    <path d="M362 176q20-24 40-12-6 26-40 12z" fill="#2fa562" />
    <text x="356" y="204" fontSize="10" fontWeight="700" fill="#6b4a2a">THE GOOD</text>
    <text x="356" y="215" fontSize="10" fontWeight="700" fill="#6b4a2a">HEALTH</text>
    <path d="M430 132v122a48 16 0 0 0 96 0V132z" fill="url(#pm-bubbles)" />
    <ellipse cx="478" cy="132" rx="48" ry="16" fill="#fff" stroke="#dbe3df" />
    <ellipse cx="478" cy="132" rx="20" ry="7" fill="#dfe6e2" />
    <ellipse cx="404" cy="240" rx="40" ry="44" fill="#c39a63" />
    <ellipse cx="410" cy="238" rx="36" ry="40" fill="#d7b27c" />
    <ellipse cx="414" cy="236" rx="20" ry="24" fill="#5b4530" />
    <ellipse cx="416" cy="235" rx="14" ry="17" fill="#3f2f20" />
  </svg>
);

const landingCards = [
  { key: "register", tone: "green", icon: <CubeIcon />, title: "Material Register", desc: "Create and manage material masters, barcode registry and stock thresholds.", primary: true },
  { key: "inventory", tone: "blue", icon: <WarehouseIcon />, title: "Inventory", desc: "Track the live stock position, batches and operational alerts.", },
  { key: "reports", tone: "purple", icon: <ChartIcon />, title: "Reports", desc: "Review daily movement, low stock, material trends and audit activity.", },
];

const statusColors = {
  Active: "green",
  Inactive: "red",
  "LOW STOCK": "orange",
  "OUT OF STOCK": "red",
  OK: "green",
};

const defaultForm = {
  materialName: "",
  materialType: "Box",
  materialCode: "",
  barcode: "",
  minimumStock: 0,
  status: "Active",
  dimensions: { length: "", width: "", height: "", thickness: "", unit: "CM" },
};

function formatDimensions(material) {
  if (!material || !material.dimensions) return "-";
  const dims = material.dimensions;
  if (material.materialType === "Box") {
    const length = dims.length ?? "";
    const width = dims.width ?? "";
    const height = dims.height ?? "";
    return [length, width, height].some((v) => v !== "" && v !== null && v !== undefined) ? `${length} × ${width} × ${height} ${dims.unit || "CM"}` : "-";
  }
  if (material.materialType === "Pouch") {
    const length = dims.length ?? "";
    const width = dims.width ?? "";
    const thickness = dims.thickness ?? "";
    return [length, width, thickness].some((v) => v !== "" && v !== null && v !== undefined) ? `${length} × ${width} × ${thickness} ${dims.unit || "CM"}` : "-";
  }
  return dims.display || "-";
}

function generateBarcode() {
  return String(Math.floor(1000000000000000 + Math.random() * 9000000000000000));
}

export default function PackingMaterialPage({ darkTheme = false, isMobile = false, heroImage = "" }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const [batches, setBatches] = useState([]);
  const [movements, setMovements] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [summary, setSummary] = useState({ totalMaterials: 0, totalCurrentStock: 0, lowStock: 0, outOfStock: 0, todaysReceipts: 0, todaysDeductions: 0, totalMaterialTypes: 0 });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ materialId: "", lotNumber: "", quantity: 1, receivedDate: "", supplier: "", referenceNumber: "", remarks: "" });
  const [form] = Form.useForm();

  const loadData = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [packingData, batchData, movementData, auditData, summaryData, alertsData] = await Promise.all([
        fetchPackingMaterials(token),
        fetchBatches(token),
        fetchMovements(token),
        fetchAuditLogs(token),
        fetchInventorySummary(token),
        fetchLowStockAlerts(token),
      ]);

      setMaterials(packingData || []);
      setBatches(batchData || []);
      setMovements(movementData || []);
      setAuditLogs(auditData || []);
      setSummary(summaryData || { totalMaterials: 0, totalCurrentStock: 0, lowStock: 0, outOfStock: 0, todaysReceipts: 0, todaysDeductions: 0, totalMaterialTypes: 0 });
      setLowStockAlerts(alertsData || []);
    } catch (error) {
      console.error("Packing material loading error:", error);
      message.error("Unable to load packing material data right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  const filteredMaterials = useMemo(() => {
    const term = search.trim().toLowerCase();
    return materials.filter((item) => {
      const matchesTerm = !term || [item.materialName, item.materialCode, item.barcode, item.materialType].join(" ").toLowerCase().includes(term);
      const matchesStatus = statusFilter === "All" || item.status === statusFilter;
      const matchesType = typeFilter === "All" || item.materialType === typeFilter;
      return matchesTerm && matchesStatus && matchesType;
    });
  }, [materials, search, statusFilter, typeFilter]);

  const materialTypes = ["All", ...new Set(materials.map((item) => item.materialType).filter(Boolean))];
  const statusOptions = ["All", "Active", "Inactive"];

  const createMaterial = async (values) => {
    try {
      const materialName = String(values.materialName || "").trim();
      if (!materialName) {
        message.error("Material name is required.");
        return;
      }

      const payload = {
        materialName,
        materialType: values.materialType,
        materialCode: String(values.materialCode || `PM-${Date.now().toString().slice(-6)}`).trim(),
        barcode: String(values.barcode || generateBarcode()).trim(),
        minimumStock: Number(values.minimumStock || 0),
        status: values.status || "Active",
        dimensions: {
          length: values.dimensions?.length || null,
          width: values.dimensions?.width || null,
          height: values.dimensions?.height || null,
          thickness: values.dimensions?.thickness || null,
          unit: values.dimensions?.unit || "CM",
          display: formatDimensions({ materialType: values.materialType, dimensions: values.dimensions }),
        },
      };
      await createPackingMaterial(token, payload);
      message.success("Material registered successfully.");
      form.resetFields();
      navigate("/packing-material/register");
      loadData();
    } catch (error) {
      const msg = error?.response?.data?.message || "Unable to create packing material.";
      message.error(msg);
    }
  };

  const onReceiveStock = async () => {
    try {
      if (!receiveForm.materialId) {
        message.error("Please select a material.");
        return;
      }
      await receiveStock(token, {
        materialId: receiveForm.materialId,
        lotNumber: receiveForm.lotNumber || `LOT-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`,
        receivedQuantity: Number(receiveForm.quantity || 0),
        receivedDate: receiveForm.receivedDate || new Date().toISOString(),
        supplier: receiveForm.supplier,
        referenceNumber: receiveForm.referenceNumber,
        remarks: receiveForm.remarks,
      });
      setShowReceiveModal(false);
      setReceiveForm({ materialId: "", lotNumber: "", quantity: 1, receivedDate: "", supplier: "", referenceNumber: "", remarks: "" });
      message.success("Stock batch received successfully.");
      loadData();
    } catch (error) {
      const msg = error?.response?.data?.message || "Unable to receive stock.";
      message.error(msg);
    }
  };

  const totalAvailable = materials.reduce((sum, item) => sum + Number(item.currentStock || 0), 0);

  const LandingPage = () => (
    <div className={`pm-landing ${darkTheme ? "dark" : "light"}`}>
      <div className="pm-shell">
        <nav className="pm-breadcrumb" aria-label="Breadcrumb">
          <span>Packing Dashboard</span>
          <RightOutlined className="pm-breadcrumb-sep" />
          <span className="pm-breadcrumb-current">Packing Material</span>
        </nav>
        <section className="pm-hero">
          <div className="pm-heading">
            <h1>Packing Material</h1>
            <p>Manage packing material master data, inventory, batches and reports.</p>
          </div>
          <div className="pm-hero-art" aria-hidden={!heroImage}>
            {heroImage ? <img src={heroImage} alt="Packing materials" /> : <HeroArt />}
          </div>
        </section>

        <Row gutter={[12, 12]} style={{ marginBottom: 18 }}>
          {[
            ["Total Material Types", summary.totalMaterialTypes],
            ["Total Current Stock", totalAvailable],
            ["Low Stock", summary.lowStock],
            ["Out of Stock", summary.outOfStock],
            ["Today's Received", summary.todaysReceipts],
            ["Today's Deducted", summary.todaysDeductions],
          ].map(([label, value]) => (
            <Col xs={12} md={8} xl={4} key={label}>
              <Card className="pm-stat-card" style={{ background: darkTheme ? "#14201c" : "#fff", border: "1px solid rgba(15, 107, 60, 0.08)" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                <Title level={4} style={{ marginTop: 8, marginBottom: 0, color: darkTheme ? "#e8fff1" : "#0f172a" }}>{value}</Title>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="pm-cards-grid">
          {landingCards.map((card) => (
            <div key={card.key} className={`pm-card tone-${card.tone}${card.primary ? " primary" : ""}`} onClick={() => navigate(`/packing-material/${card.key === "reports" ? "reports" : card.key === "inventory" ? "inventory" : "register"}`)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/packing-material/${card.key === "reports" ? "reports" : card.key === "inventory" ? "inventory" : "register"}`); }}>
              <div className="pm-card-icon">{card.icon}</div>
              <h3 className="pm-card-title">{card.title}</h3>
              <p className="pm-card-desc">{card.desc}</p>
              <span className="pm-card-go" aria-hidden="true"><ArrowRightOutlined /></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const RegisterPage = () => {
    const registerStats = [
      { label: "Total materials", value: materials.length, icon: <InboxOutlined />, tone: "green" },
      { label: "Active", value: materials.filter((item) => item.status === "Active").length, icon: <CheckCircleOutlined />, tone: "blue" },
      { label: "Need attention", value: materials.filter((item) => Number(item.currentStock || 0) < Number(item.minimumStock || 0)).length, icon: <WarningOutlined />, tone: "amber" },
      { label: "Barcodes assigned", value: materials.filter((item) => Boolean(item.barcode)).length, icon: <BarcodeOutlined />, tone: "red" },
    ];

    return (
      <div className={`pm-ops-page ${darkTheme ? "dark" : "light"}`}>
        <div className="pm-ops-shell">
          <div className="pm-ops-header">
            <div className="pm-ops-heading">
              <div className="pm-ops-breadcrumb"><span>Packing Material</span><RightOutlined /><strong>Register</strong></div>
              <Title level={2}>Material register</Title>
              <Text>Manage material details, barcode records and stock thresholds.</Text>
            </div>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => navigate("/packing-material/register/new")}>New material</Button>
          </div>

          <div className="pm-ops-metrics pm-ops-report-metrics">
            {registerStats.map((stat) => (
              <div className={`pm-ops-metric ${stat.tone}`} key={stat.label}>
                <span className="pm-ops-metric-icon">{stat.icon}</span>
                <span className="pm-ops-metric-label">{stat.label}</span>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>

          <section className="pm-ops-panel pm-register-panel">
            <div className="pm-ops-panel-head">
              <div><h3>Registered materials</h3><p>Showing {filteredMaterials.length} of {materials.length} materials</p></div>
              <InboxOutlined className="pm-ops-panel-symbol" />
            </div>

            <div className={`pm-register-toolbar ${isMobile ? "mobile" : ""}`}>
              <Search allowClear value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code or barcode" />
              <Select value={statusFilter} onChange={setStatusFilter} aria-label="Filter by status">
                {statusOptions.map((item) => <Option key={item} value={item}>{item === "All" ? "All statuses" : item}</Option>)}
              </Select>
              <Select value={typeFilter} onChange={setTypeFilter} aria-label="Filter by material type">
                {materialTypes.map((item) => <Option key={item} value={item}>{item === "All" ? "All material types" : item}</Option>)}
              </Select>
            </div>

            <Table
              dataSource={filteredMaterials}
              rowKey="_id"
              loading={loading}
              scroll={{ x: true }}
              pagination={{ pageSize: 8, showSizeChanger: false, hideOnSinglePage: true }}
              columns={[
                { title: "Material code", dataIndex: "materialCode", key: "materialCode" },
                { title: "Material name", dataIndex: "materialName", key: "materialName" },
                { title: "Type", dataIndex: "materialType", key: "materialType" },
                { title: "Dimensions", key: "dimensions", render: (_, record) => formatDimensions(record) },
                { title: "Barcode", dataIndex: "barcode", key: "barcode" },
                { title: "Minimum", dataIndex: "minimumStock", key: "minimumStock" },
                { title: "In stock", dataIndex: "currentStock", key: "currentStock" },
                { title: "Status", dataIndex: "status", key: "status", render: (status) => <Tag color={statusColors[status] || "default"}>{status}</Tag> },
                { title: "Created by", dataIndex: ["createdBy", "name"], key: "createdBy" },
                {
                  title: "Actions",
                  key: "actions",
                  fixed: isMobile ? undefined : "right",
                  render: (_, record) => (
                    <Space size={4}>
                      <Button type="link" onClick={() => navigate(`/packing-material/register/${record._id}`)}>View</Button>
                      <Button type="link" onClick={() => navigate(`/packing-material/register/new?materialId=${record._id}`)}>Edit</Button>
                    </Space>
                  ),
                },
              ]}
            />
          </section>
        </div>
      </div>
    );
  };

  const MaterialFormPage = () => {
    const materialId = new URLSearchParams(location.search).get("materialId");
    const material = materials.find((item) => item._id === materialId) || null;

    useEffect(() => {
      if (!materialId) {
        form.setFieldsValue({ ...defaultForm, barcode: generateBarcode() });
        return;
      }
      if (material) {
        form.setFieldsValue({
          materialName: material.materialName,
          materialType: material.materialType,
          materialCode: material.materialCode,
          barcode: material.barcode,
          minimumStock: material.minimumStock,
          status: material.status,
          dimensions: material.dimensions || {},
        });
      }
    }, [materialId, material, form]);

    return (
      <div className="pm-module-page" style={{ padding: isMobile ? 12 : 24 }}>
        <div className="pm-page-header">
          <div>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/packing-material/register")}>Back</Button>
            <Text type="secondary">Packing Dashboard &gt; Register</Text>
            <Title level={3} style={{ margin: "8px 0 0" }}>{materialId ? "Edit Material" : "Create Material"}</Title>
          </div>
        </div>

        <Card className="pm-panel" style={{ background: darkTheme ? "#141414" : "#fff" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={createMaterial}
            initialValues={{ ...defaultForm, barcode: generateBarcode() }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="materialName"
                  label="Material Name"
                  normalize={(value) => (typeof value === "string" ? value.trim() : value)}
                  rules={[{ required: true, whitespace: true, message: "Material name is required" }]}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="materialType" label="Material Type" initialValue="Box"> <Select> <Option value="Box">Box</Option> <Option value="Pouch">Pouch</Option> <Option value="Other">Other</Option> </Select> </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="materialCode" label="Material Code"> <Input /> </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="barcode" label="Unique Barcode"> <Input /> </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="minimumStock" label="Minimum Stock Level" initialValue={0}> <Input type="number" min={0} /> </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="status" label="Status" initialValue="Active"> <Select> <Option value="Active">Active</Option> <Option value="Inactive">Inactive</Option> </Select> </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Form.Item name={['dimensions', 'length']} label="Length"> <Input type="number" min={0} /> </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={['dimensions', 'width']} label="Width"> <Input type="number" min={0} /> </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={['dimensions', 'height']} label="Height"> <Input type="number" min={0} /> </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={['dimensions', 'thickness']} label="Thickness (if applicable)"> <Input type="number" min={0} /> </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={['dimensions', 'unit']} label="Dimension Unit" initialValue="CM"> <Select> <Option value="CM">CM</Option> <Option value="MM">MM</Option> <Option value="MICRON">MICRON</Option> </Select> </Form.Item>
              </Col>
            </Row>

            <Space>
              <Button type="primary" htmlType="submit">{materialId ? "Save Changes" : "Register Material"}</Button>
              <Button onClick={() => navigate("/packing-material/register")}>Cancel</Button>
            </Space>
          </Form>
        </Card>
      </div>
    );
  };

  const MaterialDetailView = ({ id }) => {
    const item = materials.find((entry) => entry._id === id) || null;
    if (!item) return null;

    return (
      <div className="pm-module-page" style={{ padding: isMobile ? 12 : 24 }}>
        <div className="pm-page-header">
          <div>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/packing-material/register")}>Back</Button>
            <Text type="secondary">Packing Dashboard &gt; Material</Text>
            <Title level={3} style={{ margin: "8px 0 0" }}>{item.materialName}</Title>
          </div>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card className="pm-panel" style={{ background: darkTheme ? "#141414" : "#fff" }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Material Type">{item.materialType}</Descriptions.Item>
                <Descriptions.Item label="Barcode">{item.barcode}</Descriptions.Item>
                <Descriptions.Item label="Current Stock">{item.currentStock}</Descriptions.Item>
                <Descriptions.Item label="Minimum Stock">{item.minimumStock}</Descriptions.Item>
                <Descriptions.Item label="Status"><Tag color={statusColors[item.status] || "default"}>{item.status}</Tag></Descriptions.Item>
                <Descriptions.Item label="Dimensions">{formatDimensions(item)}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card className="pm-panel" style={{ background: darkTheme ? "#141414" : "#fff" }}>
              <Title level={5}>Recent Stock Movements</Title>
              <Table
                dataSource={movements.filter((entry) => entry.materialId?._id === id || entry.materialId === id).slice(0, 8)}
                rowKey="_id"
                pagination={false}
                columns={[
                  { title: "Date", render: (_, record) => new Date(record.transactionDate).toLocaleDateString() },
                  { title: "Type", dataIndex: "transactionType" },
                  { title: "Qty", dataIndex: "quantity" },
                  { title: "Previous", dataIndex: "previousBalance" },
                  { title: "New", dataIndex: "newBalance" },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const InventoryPage = () => {
    const [inventorySearch, setInventorySearch] = useState("");
    const [inventoryType, setInventoryType] = useState("All");
    const [inventoryStatus, setInventoryStatus] = useState("All");

    const inventoryTypes = ["All", ...new Set(materials.map((item) => item.materialType).filter(Boolean))];
    const inventoryRows = useMemo(() => {
      const term = inventorySearch.trim().toLowerCase();
      return materials.filter((item) => {
        const current = Number(item.currentStock || 0);
        const minimum = Number(item.minimumStock || 0);
        const stockStatus = current <= 0 ? "Out of Stock" : current <= minimum ? "Low Stock" : "In Stock";
        const searchable = [item.materialName, item.materialCode, item.barcode, item.materialType].join(" ").toLowerCase();
        return (!term || searchable.includes(term))
          && (inventoryType === "All" || item.materialType === inventoryType)
          && (inventoryStatus === "All" || stockStatus === inventoryStatus);
      });
    }, [materials, inventorySearch, inventoryType, inventoryStatus]);

    const clearInventoryFilters = () => {
      setInventorySearch("");
      setInventoryType("All");
      setInventoryStatus("All");
    };

    const inventoryStats = [
      { label: "Materials", value: summary.totalMaterials || 0, icon: <InboxOutlined />, tone: "green" },
      { label: "Available units", value: summary.totalCurrentStock || 0, icon: <CheckCircleOutlined />, tone: "blue" },
      { label: "Low stock", value: summary.lowStock || 0, icon: <WarningOutlined />, tone: "amber" },
      { label: "Out of stock", value: summary.outOfStock || 0, icon: <CloseCircleOutlined />, tone: "red" },
      { label: "Received today", value: summary.todaysReceipts || 0, icon: <PlusOutlined />, tone: "green" },
      { label: "Issued today", value: summary.todaysDeductions || 0, icon: <SwapOutlined />, tone: "amber" },
    ];

    return (
      <div className={`pm-ops-page ${darkTheme ? "dark" : "light"}`}>
        <div className="pm-ops-shell">
          <div className="pm-ops-header">
            <div className="pm-ops-heading">
              <div className="pm-ops-breadcrumb"><span>Packing Material</span><RightOutlined /><strong>Inventory</strong></div>
              <Title level={2}>Inventory overview</Title>
              <Text>Monitor available stock, incoming batches and items that need replenishment.</Text>
            </div>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setShowReceiveModal(true)}>Receive stock</Button>
          </div>

          <div className="pm-ops-metrics">
            {inventoryStats.map((stat) => (
              <div className={`pm-ops-metric ${stat.tone}`} key={stat.label}>
                <span className="pm-ops-metric-icon">{stat.icon}</span>
                <span className="pm-ops-metric-label">{stat.label}</span>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>

          <section className="pm-ops-panel pm-inventory-panel">
            <div className="pm-ops-panel-head">
              <div><h3>Material inventory</h3><p>{inventoryRows.length} of {materials.length} materials match the current filters</p></div>
              <FilterOutlined className="pm-ops-panel-symbol" />
            </div>
            <div className="pm-register-toolbar pm-inventory-toolbar">
              <Input.Search
                allowClear
                value={inventorySearch}
                onChange={(event) => setInventorySearch(event.target.value)}
                placeholder="Search name, code or barcode"
              />
              <Select value={inventoryType} onChange={setInventoryType} aria-label="Material type filter">
                {inventoryTypes.map((type) => <Option key={type} value={type}>{type === "All" ? "All material types" : type}</Option>)}
              </Select>
              <Select value={inventoryStatus} onChange={setInventoryStatus} aria-label="Stock status filter">
                {['All', 'In Stock', 'Low Stock', 'Out of Stock'].map((status) => <Option key={status} value={status}>{status === "All" ? "All stock statuses" : status}</Option>)}
              </Select>
              {(inventorySearch || inventoryType !== "All" || inventoryStatus !== "All") && (
                <Button icon={<ClearOutlined />} onClick={clearInventoryFilters}>Clear filters</Button>
              )}
            </div>
            <Table
              dataSource={inventoryRows}
              rowKey="_id"
              size="middle"
              scroll={{ x: 960 }}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 25, 50] }}
              locale={{ emptyText: inventorySearch || inventoryType !== "All" || inventoryStatus !== "All" ? "No materials match these filters." : "No packing materials have been registered yet." }}
              columns={[
                { title: "Material", key: "material", fixed: "left", width: 210, render: (_, item) => <div className="pm-material-cell"><strong>{item.materialName || "-"}</strong><span>{item.materialCode || item.barcode || "No code"}</span></div> },
                { title: "Type", dataIndex: "materialType", key: "materialType", width: 130, render: (value) => value || "-" },
                { title: "Available", dataIndex: "currentStock", key: "currentStock", width: 105, sorter: (a, b) => Number(a.currentStock || 0) - Number(b.currentStock || 0), render: (value, item) => <strong>{Number(value || 0)} {item.unit || ""}</strong> },
                { title: "Minimum", dataIndex: "minimumStock", key: "minimumStock", width: 100, sorter: (a, b) => Number(a.minimumStock || 0) - Number(b.minimumStock || 0), render: (value) => Number(value || 0) },
                { title: "Status", key: "stockStatus", width: 125, render: (_, item) => { const current = Number(item.currentStock || 0); const minimum = Number(item.minimumStock || 0); const status = current <= 0 ? "Out of Stock" : current <= minimum ? "Low Stock" : "In Stock"; return <Tag color={status === "Out of Stock" ? "red" : status === "Low Stock" ? "orange" : "green"}>{status}</Tag>; } },
                { title: "Record status", dataIndex: "status", key: "status", width: 110, render: (value) => <Tag color={value === "Active" ? "green" : "default"}>{value || "-"}</Tag> },
              ]}
            />
          </section>

          <div className="pm-ops-grid">
            <section className="pm-ops-panel">
              <div className="pm-ops-panel-head">
                <div><h3>Replenishment watch</h3><p>{lowStockAlerts.length} items need attention</p></div>
                <WarningOutlined className="pm-ops-panel-symbol" />
              </div>
              {lowStockAlerts.length === 0 ? (
                <div className="pm-ops-empty"><CheckCircleOutlined /><span>All materials are above their minimum stock level.</span></div>
              ) : (
                <div className="pm-ops-alert-list">
                  {lowStockAlerts.map((alert) => (
                    <div key={alert.materialId} className="pm-ops-alert-row">
                      <div className="pm-ops-alert-copy">
                        <strong>{alert.materialName}</strong>
                        <span>{alert.currentStock} available <i /> Minimum {alert.minimumStock}</span>
                      </div>
                      <Tag color={statusColors[alert.status] || "default"}>{alert.status}</Tag>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="pm-ops-panel">
              <div className="pm-ops-panel-head">
                <div><h3>Recent movements</h3><p>Latest stock activity across materials</p></div>
                <BarChartOutlined className="pm-ops-panel-symbol" />
              </div>
              <Table
                dataSource={movements.slice(0, 5)}
                pagination={false}
                rowKey="_id"
                scroll={{ x: true }}
                columns={[
                  { title: "Date", render: (_, record) => new Date(record.transactionDate).toLocaleDateString() },
                  { title: "Material", render: (_, record) => record.materialId?.materialName || "-" },
                  { title: "Movement", dataIndex: "transactionType", render: (type) => <Tag color={type === "RECEIPT" || type === "RETURN" ? "green" : type === "DEDUCTION" ? "orange" : "blue"}>{type}</Tag> },
                  { title: "Qty", dataIndex: "quantity" },
                ]}
              />
            </section>
          </div>

          <section className="pm-ops-panel pm-ops-batches">
            <div className="pm-ops-panel-head">
              <div><h3>Batch register</h3><p>Recent lots received into packing inventory</p></div>
              <InboxOutlined className="pm-ops-panel-symbol" />
            </div>
            <Table
              dataSource={batches.slice(0, 10)}
              rowKey="_id"
              pagination={false}
              scroll={{ x: true }}
              columns={[
                { title: "Batch / lot", dataIndex: "lotNumber" },
                { title: "Material", render: (_, record) => record.materialId?.materialName || "-" },
                { title: "Received", render: (_, record) => new Date(record.receivedDate).toLocaleDateString() },
                { title: "Received qty", dataIndex: "receivedQuantity" },
                { title: "Remaining", dataIndex: "remainingQuantity" },
                { title: "Created by", render: (_, record) => record.createdBy?.name || "-" },
              ]}
            />
          </section>
        </div>
      </div>
    );
  };

  const ReportsPage = () => {
    const [reportSearch, setReportSearch] = useState("");
    const [reportType, setReportType] = useState("All");
    const [transactionType, setTransactionType] = useState("All");
    const [reportRange, setReportRange] = useState(null);
    const [reportView, setReportView] = useState("usage");

    const movementMaterial = (record) => {
      if (record.materialId && typeof record.materialId === "object") return record.materialId;
      return materials.find((item) => item._id === record.materialId || item.id === record.materialId || item.materialCode === record.materialId) || null;
    };
    const filteredMovements = useMemo(() => movements.filter((record) => {
      const material = movementMaterial(record);
      const searchable = [material?.materialName, material?.materialCode, record.orderId, record.packerName, record.operatorName, record.createdBy?.name].join(" ").toLowerCase();
      const date = record.transactionDate ? new Date(record.transactionDate).getTime() : null;
      const inRange = !reportRange || !date || ((!reportRange[0] || date >= reportRange[0].startOf("day").valueOf()) && (!reportRange[1] || date <= reportRange[1].endOf("day").valueOf()));
      return (!reportSearch.trim() || searchable.includes(reportSearch.trim().toLowerCase()))
        && (reportType === "All" || material?.materialType === reportType)
        && (transactionType === "All" || record.transactionType === transactionType)
        && inRange;
    }), [movements, materials, reportSearch, reportType, transactionType, reportRange]);

    const reportTypes = ["All", ...new Set(materials.map((item) => item.materialType).filter(Boolean))];
    const movementTypes = ["All", ...new Set(movements.map((item) => item.transactionType).filter(Boolean))];
    const totalConsumed = filteredMovements.filter((item) => item.transactionType === "DEDUCTION").reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const consumedMaterials = new Set(filteredMovements.filter((item) => item.transactionType === "DEDUCTION").map((item) => movementMaterial(item)?._id || movementMaterial(item)?.materialCode)).size;
    const reportStats = [
      { label: "Matching movements", value: filteredMovements.length, icon: <BarChartOutlined />, tone: "green" },
      { label: "Quantity consumed", value: totalConsumed, icon: <SwapOutlined />, tone: "amber" },
      { label: "Materials consumed", value: consumedMaterials, icon: <InboxOutlined />, tone: "blue" },
      { label: "Affected orders", value: new Set(filteredMovements.filter((item) => item.transactionType === "DEDUCTION").map((item) => item.orderId).filter(Boolean)).size, icon: <CheckCircleOutlined />, tone: "red" },
    ];

    const materialSummary = useMemo(() => {
      const grouped = new Map();
      filteredMovements.forEach((record) => {
        const material = movementMaterial(record);
        const key = material?._id || material?.materialCode || material?.materialName || "Unknown";
        const current = grouped.get(key) || { key, name: material?.materialName || "Unknown material", code: material?.materialCode || "-", type: material?.materialType || "-", consumed: 0, movements: 0, stock: material?.currentStock };
        current.movements += 1;
        if (record.transactionType === "DEDUCTION") current.consumed += Number(record.quantity || 0);
        grouped.set(key, current);
      });
      return [...grouped.values()].sort((a, b) => b.consumed - a.consumed);
    }, [filteredMovements]);

    const trendRows = useMemo(() => {
      const grouped = new Map();
      filteredMovements.filter((record) => record.transactionType === "DEDUCTION").forEach((record) => {
        const key = record.transactionDate ? new Date(record.transactionDate).toLocaleDateString() : "Unknown date";
        grouped.set(key, (grouped.get(key) || 0) + Number(record.quantity || 0));
      });
      return [...grouped.entries()].map(([date, quantity]) => ({ date, quantity }));
    }, [filteredMovements]);

    const clearReportFilters = () => { setReportSearch(""); setReportType("All"); setTransactionType("All"); setReportRange(null); };
    const handleReportExport = () => {
      if (!filteredMovements.length) { message.warning("No matching movements to export"); return; }
      const rows = filteredMovements.map((record) => { const material = movementMaterial(record); return [record.transactionDate || "", material?.materialCode || "", material?.materialName || "", material?.materialType || "", record.transactionType || "", record.quantity || "", record.orderId || "", record.previousBalance ?? "", record.newBalance ?? ""]; });
      const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const csv = [["Date", "Material code", "Material name", "Material type", "Transaction", "Quantity", "Order ID", "Previous balance", "New balance"], ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a"); link.href = url; link.download = `packing-material-movements-${new Date().toISOString().slice(0, 10)}.csv`; link.click(); URL.revokeObjectURL(url);
      message.success(`Exported ${filteredMovements.length} movements`);
    };

    const movementColumns = [
      { title: "Date and time", key: "date", dataIndex: "transactionDate", sorter: (a, b) => new Date(a.transactionDate || 0) - new Date(b.transactionDate || 0), render: (value) => value ? new Date(value).toLocaleString() : "-" },
      { title: "Material", key: "material", render: (_, record) => { const material = movementMaterial(record); return <div className="pm-material-cell"><strong>{material?.materialName || "-"}</strong><span>{material?.materialCode || ""}</span></div>; } },
      { title: "Type", dataIndex: "transactionType", key: "transactionType", render: (value) => <Tag color={value === "DEDUCTION" ? "orange" : value === "RECEIPT" || value === "RETURN" ? "green" : "blue"}>{value || "-"}</Tag> },
      { title: "Quantity", dataIndex: "quantity", key: "quantity", sorter: (a, b) => Number(a.quantity || 0) - Number(b.quantity || 0) },
      { title: "Order ID", dataIndex: "orderId", key: "orderId", render: (value) => value || "-" },
      { title: "Previous", dataIndex: "previousBalance", key: "previousBalance", render: (value) => value ?? "-" },
      { title: "Balance after", dataIndex: "newBalance", key: "newBalance", render: (value) => value ?? "-" },
    ];

    return (
      <div className={`pm-ops-page ${darkTheme ? "dark" : "light"}`}>
        <div className="pm-ops-shell">
          <div className="pm-ops-header">
            <div className="pm-ops-heading">
              <div className="pm-ops-breadcrumb"><span>Packing Material</span><RightOutlined /><strong>Reports</strong></div>
              <Title level={2}>Movement reports</Title>
              <Text>Review stock changes and balances across your packing materials.</Text>
            </div>
              <Space>
                <Button icon={<ExportOutlined />} onClick={handleReportExport}>Export filtered CSV</Button>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/packing-material")}>Back to overview</Button>
              </Space>
          </div>

          <div className="pm-ops-metrics pm-ops-report-metrics">
            {reportStats.map((stat) => (
              <div className={`pm-ops-metric ${stat.tone}`} key={stat.label}>
                <span className="pm-ops-metric-icon">{stat.icon}</span>
                <span className="pm-ops-metric-label">{stat.label}</span>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>

          <section className="pm-ops-panel">
            <div className="pm-ops-panel-head pm-ops-report-head">
              <div><h3>Material reporting</h3><p>{filteredMovements.length} of {movements.length} movement records match the current filters</p></div>
              <RangePicker value={reportRange} onChange={setReportRange} allowClear placeholder={["Start date", "End date"]} />
            </div>
            <div className="pm-register-toolbar pm-report-toolbar">
              <Input.Search allowClear value={reportSearch} onChange={(event) => setReportSearch(event.target.value)} placeholder="Search material, code or order" />
              <Select value={reportType} onChange={setReportType}>{reportTypes.map((type) => <Option key={type} value={type}>{type === "All" ? "All material types" : type}</Option>)}</Select>
              <Select value={transactionType} onChange={setTransactionType}>{movementTypes.map((type) => <Option key={type} value={type}>{type === "All" ? "All transaction types" : type}</Option>)}</Select>
              {(reportSearch || reportType !== "All" || transactionType !== "All" || reportRange) && <Button icon={<ClearOutlined />} onClick={clearReportFilters}>Clear filters</Button>}
            </div>
            <div className="pm-report-tabs" role="tablist" aria-label="Report views">
              {[['usage', 'Usage summary'], ['trend', 'Consumption trend'], ['stock', 'Stock movement']].map(([key, label]) => <Button key={key} type={reportView === key ? "primary" : "text"} onClick={() => setReportView(key)}>{label}</Button>)}
            </div>
            {reportView === "usage" && <Table dataSource={materialSummary} rowKey="key" pagination={{ pageSize: 10, showSizeChanger: true }} scroll={{ x: 760 }} locale={{ emptyText: "No material usage matches the selected filters." }} columns={[
              { title: "Material", key: "material", render: (_, row) => <div className="pm-material-cell"><strong>{row.name}</strong><span>{row.code}</span></div> },
              { title: "Type", dataIndex: "type", key: "type" },
              { title: "Movements", dataIndex: "movements", key: "movements" },
              { title: "Consumed", dataIndex: "consumed", key: "consumed", sorter: (a, b) => a.consumed - b.consumed },
              { title: "Current stock", dataIndex: "stock", key: "stock", render: (value) => value ?? "-" },
            ]} />}
            {reportView === "trend" && <Table dataSource={trendRows} rowKey="date" pagination={{ pageSize: 10 }} locale={{ emptyText: "No deduction trend data matches the selected filters." }} columns={[{ title: "Date", dataIndex: "date", key: "date" }, { title: "Quantity consumed", dataIndex: "quantity", key: "quantity", sorter: (a, b) => a.quantity - b.quantity }]} />}
            {reportView === "stock" && <Table dataSource={filteredMovements} rowKey="_id" pagination={{ pageSize: 10, showSizeChanger: true }} scroll={{ x: 1100 }} locale={{ emptyText: "No stock movements match the selected filters." }} columns={movementColumns} />}
          </section>
        </div>
      </div>
    );
  };

  const AuditPage = () => (
    <div className="pm-module-page" style={{ padding: isMobile ? 12 : 24 }}>
      <div className="pm-page-header">
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/packing-material")}>Back</Button>
          <Text type="secondary">Packing Dashboard &gt; Audit</Text>
          <Title level={3} style={{ margin: "8px 0 0" }}>Audit History</Title>
        </div>
      </div>

      <Card className="pm-panel" style={{ background: darkTheme ? "#141414" : "#fff" }}>
        <Table dataSource={auditLogs} rowKey="_id" pagination={{ pageSize: 8 }} columns={[{ title: "Action", dataIndex: "action" }, { title: "Material", dataIndex: "materialName" }, { title: "Batch", dataIndex: "batchNumber" }, { title: "Type", dataIndex: "transactionType" }, { title: "User", dataIndex: "userName" }, { title: "Time", render: (_, record) => new Date(record.timestamp).toLocaleString() }]} />
      </Card>
    </div>
  );

  const MaterialDetailRoute = () => {
    const { materialId } = useParams();
    return <MaterialDetailView id={materialId} />;
  };

  return (
    <div className={`pm-module-root ${darkTheme ? "dark" : "light"}`}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/new" element={<MaterialFormPage />} />
        <Route path="/register/:materialId" element={<MaterialDetailRoute />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/audit" element={<AuditPage />} />
      </Routes>

      <Modal title="Receive Stock" open={showReceiveModal} onCancel={() => setShowReceiveModal(false)} onOk={onReceiveStock} okText="Receive Stock" width={720}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select value={receiveForm.materialId} onChange={(value) => setReceiveForm((prev) => ({ ...prev, materialId: value }))} style={{ width: "100%" }} placeholder="Select material">
            {materials.map((material) => (
              <Option key={material._id} value={material._id}>{material.materialName} ({material.barcode})</Option>
            ))}
          </Select>
          <Input value={receiveForm.lotNumber} onChange={(e) => setReceiveForm((prev) => ({ ...prev, lotNumber: e.target.value }))} placeholder="Batch / Lot Number" />
          <Input type="number" value={receiveForm.quantity} onChange={(e) => setReceiveForm((prev) => ({ ...prev, quantity: e.target.value }))} placeholder="Received Quantity" />
          <Input type="date" value={receiveForm.receivedDate?.slice(0, 10) || ""} onChange={(e) => setReceiveForm((prev) => ({ ...prev, receivedDate: new Date(e.target.value).toISOString() }))} />
          <Input value={receiveForm.supplier} onChange={(e) => setReceiveForm((prev) => ({ ...prev, supplier: e.target.value }))} placeholder="Supplier / Source" />
          <Input value={receiveForm.referenceNumber} onChange={(e) => setReceiveForm((prev) => ({ ...prev, referenceNumber: e.target.value }))} placeholder="Reference Number" />
          <Input.TextArea value={receiveForm.remarks} onChange={(e) => setReceiveForm((prev) => ({ ...prev, remarks: e.target.value }))} placeholder="Remarks" rows={3} />
        </Space>
      </Modal>
    </div>
  );
}
